import 'molstar/build/viewer/molstar.css';
import { OrderedSet } from 'molstar/lib/mol-data/int';
import { StructureElement, StructureProperties } from 'molstar/lib/mol-model/structure';
import { Structure } from 'molstar/lib/mol-model/structure/structure/structure';
import { InteractivityManager } from 'molstar/lib/mol-plugin-state/manager/interactivity';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { DefaultPluginUISpec, PluginUISpec } from 'molstar/lib/mol-plugin-ui/spec';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { PluginConfig } from 'molstar/lib/mol-plugin/config';
import { PluginLayoutControlsDisplay } from 'molstar/lib/mol-plugin/layout';
import { useEffect, useRef, useState } from 'react';
import {
   ControlState,
   getDefaultChainColor,
   getDefaultPocketColor,
   getDefaultRepresentation,
   MolstarActionPayload,
   MolstarRepresentationChangePayload,
   MolstarResidueFocusPayload,
   MolstarTransparencyPayload,
   MolstarUniprotHoverPayload,
   useAhojData,
   useStructureEventBus,
   useStructureState,
} from '../../contexts/AhojContext';
import { getDefaultLigandColor, getStructureTypeGroup, StructureType } from '../../contexts/ControlTypes';
import { ApoHoloSiteDto } from '../../models/ApoHoloSiteDto';
import { ColorPairDto } from '../../models/ColorPairDto';
import { PocketResiduesResult } from '../../models/PocketResiduesResult';
import '../../styles/molstar-theme.css';
import { AhojMolstarWrapper } from './AhojMolstarWrapper';
import {
   defaultLigandRepresentation,
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from './Controls/StructureRepresentationComponent';
import { NoWaterPreset } from './Presets/NoWaterPreset';
import { ViewerAutoPreset } from './Presets/ViewerAutoPreset';

/**
 * React bridge component that owns the Molstar `PluginUIContext` and wires the
 * `StructureEventBusContext` actions to `AhojMolstarWrapper` method calls.
 *
 * Lifecycle overview:
 * 1. `init()` runs once on mount: creates the Molstar plugin, builds
 *    `AhojMolstarWrapper`, loads the initial batch of structures, applies default
 *    representations, and subscribes to viewport click events.
 * 2. `subscribeVisibilityChange()` handles `TOGGLE_VISIBILITY`, `FOCUS`,
 *    `POCKET_FOCUS`, `RESIDUE_FOCUS`, `TOGGLE_WATER`, `SET_TRANSPARENCY`,
 *    `UNIPROT_HOVER`, and `SELECT_UNIPROT_POSITION` actions.
 * 3. `subscribeRepresentationChange()` handles global and per-row repr/color changes.
 *
 * Important refs:
 * - `wrapperRef`       – holds the `AhojMolstarWrapper` instance (not React state so
 *                        setting it doesn't trigger a re-render).
 * - `controlsMapRef`   – mirror of `controlsMap` React state updated by a `useEffect`
 *                        so the async subscription closure always sees the latest state
 *                        without needing the state in its dependency array.
 * - `globalSettingsRef`– tracks the current global repr/color so newly loaded
 *                        structures receive the right defaults without re-subscribing.
 * - `selectedRowIdsRef`– tracks which row IDs have active camera focus so they can
 *                        be re-selected after a deselect-all.
 */
const MolstarComponent = (): React.JSX.Element => {
   const { apoHoloSites, residueMapping } = useAhojData();
   const { controlsMap, setControl } = useStructureState();
   const {
      subscribe,
      subscribeRepresentationChange: subscribeToRepresentationChange,
      dispatchStructureClick,
      dispatchMolstarHover,
   } = useStructureEventBus();
   const wrapperRef = useRef<AhojMolstarWrapper | null>(null);
   const controlsMapRef = useRef<Map<string, ControlState>>(new Map());
   const selectedRowIdsRef = useRef<Set<string>>(new Set());
   const globalSettingsRef = useRef({
      pocketRepresentation: getDefaultRepresentation().get('pocket') as Representation,
      restRepresentation: getDefaultRepresentation().get('chain') as Representation,
      ligandRepresentation: defaultLigandRepresentation,
      ligandColor: getDefaultLigandColor(),
      colors: new Map<StructureType, ColorPairDto>([
         ['apo', { pocketColor: getDefaultPocketColor('apo'), restColor: getDefaultChainColor('apo') } as ColorPairDto],
         [
            'holo',
            { pocketColor: getDefaultPocketColor('holo'), restColor: getDefaultChainColor('holo') } as ColorPairDto,
         ],
         [
            'unobserved',
            {
               pocketColor: getDefaultPocketColor('unobserved'),
               restColor: getDefaultChainColor('unobserved'),
            } as ColorPairDto,
         ],
         [
            'query_pocket',
            {
               pocketColor: getDefaultPocketColor('query_pocket'),
               restColor: getDefaultChainColor('query_pocket'),
            } as ColorPairDto,
         ],
         [
            'alphafold',
            {
               pocketColor: getDefaultPocketColor('alphafold'),
               restColor: getDefaultChainColor('alphafold'),
            } as ColorPairDto,
         ],
      ]),
   });
   /**
    * Returns a fresh default representation Map with the ligand entry included.
    * Called when initializing `ControlState` for a newly loaded row so that all
    * three component types have a valid initial representation.
    */
   const createDefaultRepresentationMap = (): Map<StructureComponentType, Representation> => {
      const map = getDefaultRepresentation();
      map.set('ligand', defaultLigandRepresentation);
      return map;
   };
   const defaultSpec = DefaultPluginUISpec();
   /**
    * Removes the `camera-focus-loci` and `representation-focus-loci` behaviors from
    * the default Molstar spec. These behaviors cause the camera to auto-zoom on every
    * single-click, which conflicts with AHoJ's manual focus model. Double-click reset
    * is re-added manually after plugin creation.
    */
   const filteredBehaviors = (defaultSpec.behaviors ?? []).filter(
      (b) =>
         b.transformer.definition.name !== 'camera-focus-loci' &&
         b.transformer.definition.name !== 'representation-focus-loci',
   );
   const MySpec: PluginUISpec = {
      ...defaultSpec,
      behaviors: filteredBehaviors,
      layout: {
         ...defaultSpec.layout,
         initial: {
            ...defaultSpec.layout?.initial,
            isExpanded: false,
            showControls: false,
            controlsDisplay: 'reactive' as PluginLayoutControlsDisplay,
            regionState: {
               bottom: 'hidden',
               left: 'collapsed',
               right: 'full',
               top: 'hidden',
            },
         },
      },
      components: {
         ...defaultSpec.components,
         controls: {
            ...defaultSpec.components?.controls,
            top: 'none',
            bottom: 'none',
         },
         remoteState: 'none',
      },
      config: [
         ...(defaultSpec.config || []),
         [PluginConfig.Viewport.ShowSelectionMode, false],
         [PluginConfig.Viewport.ShowAnimation, false],
         [PluginConfig.Structure.DefaultRepresentationPreset, NoWaterPreset.id],
      ],
   };
   const [, setPlugin] = useState<AhojMolstarWrapper>({} as AhojMolstarWrapper);

   /**
    * Initializes the Molstar plugin, creates the wrapper, and loads the initial
    * batch of structures in chunks of 5.
    *
    * Loading priority order (earlier entries appear first in the viewer):
    *  1. AlphaFold models
    *  2. Query pocket structures
    *  3. First 10 unobserved
    *  4. First 10 holo
    *  5. First 10 apo
    *
    * After each batch, per-structure pocket + chain + ligand representations are
    * applied using `wrapper.updateByComponentType`. The ligand is always set up so
    * it is not accidentally overwritten by subsequent pocket/chain changes.
    *
    * Finally, re-adds the double-click camera reset that was removed along with the
    * auto-focus behaviors.
    */
   const init = async (): Promise<void> => {
      const parent = document.getElementById('molstar-parent') as HTMLDivElement;

      const pluginContext = await createPluginUI({
         target: parent,
         spec: MySpec,
         render: renderReact18,
         onBeforeUIRender: (context) => {
            context.builders.structure.representation.registerPreset(ViewerAutoPreset);
            context.builders.structure.representation.registerPreset(NoWaterPreset);
         },
      });

      const wrapper = new AhojMolstarWrapper(pluginContext);
      wrapper.apoHoloSites = [
         ...apoHoloSites.apo_sites,
         ...apoHoloSites.holo_sites,
         ...apoHoloSites.unobserved_sites,
         ...apoHoloSites.query_pocket_sites,
         ...apoHoloSites.alphafold_sites,
      ];

      wrapperRef.current = wrapper;
      setPlugin(wrapper);

      const toLoad = [
         ...apoHoloSites.alphafold_sites,
         ...apoHoloSites.query_pocket_sites,
         ...apoHoloSites.unobserved_sites.slice(0, 10),
         ...apoHoloSites.holo_sites.slice(0, 10),
         ...apoHoloSites.apo_sites.slice(0, 10),
      ];
      const batches = chunkArray(toLoad, 5);

      for (const chunk of batches) {
         await wrapper.loadStructures(chunk);
         for (const site of chunk) {
            const label = site.url.split('/').pop() || '';
            if (!wrapper.structureReferences.get(label)) continue;

            setControl(site.id, {
               isVisible: true,
               isSelected: false,
               selectedRepresentations: createDefaultRepresentationMap(),
               pocketColor: getDefaultPocketColor(site.type),
               chainColor: getDefaultChainColor(site.type),
               ligandColor: getDefaultLigandColor(),
               structureType: site.type,
            } as ControlState);

            try {
               const pocketResidues = residueMapping.get(site.id);
               if (pocketResidues && pocketResidues.residues.length > 0) {
                  await wrapper.updateByComponentType(
                     label,
                     defaultPocketRepresentation,
                     getDefaultPocketColor(site.type),
                     pocketResidues,
                     'pocket',
                     true,
                     true,
                  );
                  await wrapper.updateByComponentType(
                     label,
                     defaultRestRepresentation,
                     getDefaultChainColor(site.type),
                     pocketResidues,
                     'chain',
                     false,
                     true,
                  );
               } else {
                  await wrapper.updateByComponentType(
                     label,
                     defaultRestRepresentation,
                     getDefaultChainColor(site.type),
                     { residues: [] },
                     undefined,
                     false,
                     true,
                  );
               }
               await wrapper.updateByComponentType(
                  label,
                  globalSettingsRef.current.ligandRepresentation,
                  globalSettingsRef.current.ligandColor,
                  pocketResidues ?? { residues: [] },
                  'ligand',
                  true,
                  true,
               );
            } catch (err) {
               console.error('Failed to apply default representation for', label, err);
            }
         }
      }

      subscribeToOnStructureClick();
      subscribeToOnStructureHover();

      // Restore the default Molstar double-click camera reset.
      // camera-focus-loci was removed to prevent single-click auto-zoom,
      // but that also disabled the double-click reset — re-add it manually.
      const canvas = pluginContext.canvas3d?.webgl.gl.canvas as HTMLElement | undefined;
      canvas?.addEventListener('dblclick', () => {
         void PluginCommands.Camera.Reset(pluginContext, {});
      });
   };

   /**
    * Subscribes to Molstar's viewport click event. When a structure is clicked:
    * 1. Extracts the `Structure` from the loci.
    * 2. Resolves the structure label (= filename key) by walking the structure's parent
    *    chain until a match is found in `structureReferences`.
    * 3. Dispatches a `structureClick` event on the bus so tables can sync their
    *    selection state and scroll the row into view.
    */
   const subscribeToOnStructureClick = (): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper || !wrapper.plugin) return;

      wrapper.plugin.behaviors.interaction.click.subscribe((event: InteractivityManager.ClickEvent) => {
         const loci = event.current.loci;

         let structure: Structure | undefined;
         if (StructureElement.Loci.is(loci)) {
            structure = loci.structure;
         }

         if (!structure) return;

         const label = wrapper.getLabelFromStructure(structure);
         if (label === undefined) return;

         const struct = wrapper.structureReferences.get(label);
         if (!struct) return;

         dispatchStructureClick(struct.site);
      });
   };

   /**
    * Subscribes to Molstar's viewport hover event. When the cursor moves over a
    * residue, extracts its `auth_seq_id` and dispatches it via `dispatchMolstarHover`
    * so the RCSB sequence viewer can highlight the same position.
    * A 30 ms debounce prevents redundant updates during rapid mouse movement.
    * When the cursor leaves a residue (empty loci), `null` is dispatched to clear.
    */
   const molstarHoverDebounceRef = { current: null as ReturnType<typeof setTimeout> | null };
   const subscribeToOnStructureHover = (): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper || !wrapper.plugin) return;

      wrapper.plugin.behaviors.interaction.hover.subscribe((event: InteractivityManager.HoverEvent) => {
         if (molstarHoverDebounceRef.current !== null) clearTimeout(molstarHoverDebounceRef.current);

         const loci = event.current.loci;
         if (!StructureElement.Loci.is(loci) || StructureElement.Loci.isEmpty(loci)) {
            molstarHoverDebounceRef.current = setTimeout(() => {
               dispatchMolstarHover(null);
            }, 30);
            return;
         }

         molstarHoverDebounceRef.current = setTimeout(() => {
            const firstEl = loci.elements[0];
            if (!firstEl) {
               dispatchMolstarHover(null);
               return;
            }
            const unit = firstEl.unit;
            const index = OrderedSet.getAt(firstEl.indices, 0);
            const loc = StructureElement.Location.create(loci.structure, unit, unit.elements[index]);
            const seqId = StructureProperties.residue.auth_seq_id(loc);
            dispatchMolstarHover(seqId);
         }, 30);
      });
   };

   /**
    * Splits an array into sub-arrays of `chunkSize`. Used to load structures in
    * small batches so the browser remains interactive during the initial load.
    */
   const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
      const chunks: T[][] = [];
      for (let i = 0; i < array.length; i += chunkSize) {
         chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
   };

   /**
    * Subscribes to the generic `MolstarAction` bus channel and routes each action
    * type to the appropriate `AhojMolstarWrapper` method.
    *
    * Tricky cases:
    * - `TOGGLE_VISIBILITY` with `focusAfterLoad: true`: the structure may not be loaded
    *   yet; `onLoaded` is called by `toggleCellVisibility` once loading completes and
    *   applies the current global representation before focusing.
    * - `FOCUS` with `value: false`: calls `deselectAll`, then re-selects all rows in
    *   `selectedRowIdsRef` to preserve other active selections.
    */
   const subscribeVisibilityChange = (): (() => void) => {
      return subscribe((action) => {
         const wrapper = wrapperRef.current;
         if (!wrapper || !wrapper.plugin) return;

         if (action.type === 'TOGGLE_VISIBILITY' && action.payload) {
            const payload = action.payload as MolstarActionPayload;
            const label = payload.url.split('/').pop() || '';

            const onLoaded = async (site: ApoHoloSiteDto): Promise<void> => {
               const w = wrapperRef.current;
               if (!w) return;
               const settings = globalSettingsRef.current;
               const rowState = controlsMapRef.current.get(payload.rowId);
               // Prefer the existing row state (e.g. after hide/show cycle), then the
               // current per-group global default, then the hard-coded initial default.
               const groupDefaults = settings.colors.get(site.type);
               const pocketColor =
                  rowState?.pocketColor ?? groupDefaults?.pocketColor ?? getDefaultPocketColor(site.type);
               const restColor = rowState?.chainColor ?? groupDefaults?.restColor ?? getDefaultChainColor(site.type);
               const ligandColor = rowState?.ligandColor ?? settings.ligandColor;
               const pocketResidues = residueMapping.get(payload.rowId);
               if (pocketResidues && pocketResidues.residues.length > 0) {
                  await w.updateByComponentType(
                     label,
                     settings.pocketRepresentation,
                     pocketColor,
                     pocketResidues,
                     'pocket',
                     true,
                     true,
                  );
                  await w.updateByComponentType(
                     label,
                     settings.restRepresentation,
                     restColor,
                     pocketResidues,
                     'chain',
                     true,
                     true,
                  );
               } else {
                  await w.updateByComponentType(
                     label,
                     settings.restRepresentation,
                     restColor,
                     { residues: [] },
                     undefined,
                     true,
                     true,
                  );
               }
               await w.updateByComponentType(
                  label,
                  settings.ligandRepresentation,
                  ligandColor,
                  pocketResidues ?? { residues: [] },
                  'ligand',
                  true,
                  true,
               );
               const reprMap = new Map<StructureComponentType, Representation>([
                  ['pocket', settings.pocketRepresentation],
                  ['chain', settings.restRepresentation],
                  ['ligand', settings.ligandRepresentation],
               ]);
               setControl(payload.rowId, {
                  isVisible: true,
                  isSelected: false,
                  selectedRepresentations: reprMap,
                  pocketColor,
                  chainColor: restColor,
                  ligandColor,
                  structureType: site.type,
               } as ControlState);
               if (payload.focusAfterLoad) {
                  selectedRowIdsRef.current.add(payload.rowId);
                  w.toggleFocus(label, true);
               }
            };

            const alreadyLoaded = wrapper.structureReferences.has(label);
            wrapper
               .toggleCellVisibility(label, payload.value, onLoaded)
               .then(() => {
                  if (payload.focusAfterLoad && alreadyLoaded) {
                     selectedRowIdsRef.current.add(payload.rowId);
                     wrapper.toggleFocus(label, true);
                  }
               })
               .catch((err) => {
                  console.error('toggleCellVisibility failed:', err);
               });
         } else if (action.type === 'FOCUS' && action.payload) {
            const payload = action.payload as MolstarActionPayload;
            const label = payload.url.split('/').pop() || '';
            if (payload.value) {
               selectedRowIdsRef.current.add(payload.rowId);
               wrapper.toggleFocus(label, true);
            } else {
               selectedRowIdsRef.current.delete(payload.rowId);
               wrapper.deselectAll();
               // Re-select structures that are still selected
               selectedRowIdsRef.current.forEach((rowId) => {
                  const site = wrapper.apoHoloSites.find((s) => s.id === rowId);
                  if (site) {
                     const siteLabel = site.url.split('/').pop() || '';
                     wrapper.toggleFocus(siteLabel, true);
                  }
               });
            }
         } else if (action.type === 'POCKET_FOCUS' && action.payload) {
            const payload = action.payload as MolstarActionPayload;
            const label = payload.url.split('/').pop() || '';
            const pocketResidues = residueMapping.get(payload.rowId);
            if (pocketResidues) {
               const residueIds = pocketResidues.residues.map((r) => parseInt(r)).filter((r) => !isNaN(r));
               wrapper.focusPocketResidues(label, residueIds, payload.value);
            }
         } else if (action.type === 'RESIDUE_FOCUS' && action.payload) {
            const payload = action.payload as MolstarResidueFocusPayload;
            const label = payload.url.split('/').pop() || '';
            wrapper.focusSingleResidue(label, payload.residueSeqId, payload.value);
         } else if (action.type === 'TOGGLE_WATER' && action.payload) {
            const waterPayload = action.payload as MolstarActionPayload;
            wrapper.toggleWater(waterPayload.value).catch((err) => {
               console.error('toggleWater failed:', err);
            });
         } else if (action.type === 'SET_TRANSPARENCY' && action.payload) {
            const payload = action.payload as MolstarTransparencyPayload;
            const label = payload.url.split('/').pop() || '';
            wrapper.setStructureTransparency(label, payload.alpha, payload.componentType).catch((err) => {
               console.error('setStructureTransparency failed:', err);
            });
         } else if (action.type === 'UNIPROT_HOVER') {
            const payload = action.payload as MolstarUniprotHoverPayload;
            wrapper.highlightUniprotPosition(payload?.uniprotPos ?? null);
         } else if (action.type === 'SELECT_UNIPROT_POSITION') {
            const payload = action.payload as MolstarUniprotHoverPayload;
            wrapper.selectUniprotPosition(payload?.uniprotPos ?? null);
         }
      });
   };

   /**
    * Updates the per-type color entry in `globalSettingsRef.colors` so that newly
    * loaded structures receive the correct current default instead of the hard-coded
    * initial value. Also mirrors holo-group changes to `unobserved` and `query_pocket`
    * since they share the same color group. When `structureType` is `undefined` (global
    * all-structures event), the color is applied to every group entry.
    */
   const assignGlobalColor = (
      structureType: StructureType | undefined,
      componentType: StructureComponentType,
      color: string,
   ): void => {
      if (componentType === 'ligand') {
         globalSettingsRef.current.ligandColor = color;
         return;
      }
      if (!structureType) {
         // Global event — update every group so future loads get the right default.
         globalSettingsRef.current.colors.forEach((entry) => {
            if (componentType === 'pocket') entry.pocketColor = color;
            else if (componentType === 'chain') entry.restColor = color;
         });
         return;
      }
      const entry = globalSettingsRef.current.colors.get(structureType);
      if (entry) {
         if (componentType === 'pocket') entry.pocketColor = color;
         else if (componentType === 'chain') entry.restColor = color;
      }
      // Mirror holo-group changes to the other holo-group types
      if (structureType === 'holo') {
         assignColorsToStructureType('unobserved', componentType, color);
         assignColorsToStructureType('query_pocket', componentType, color);
      }
   };

   const assignColorsToStructureType = (
      structureType: StructureType,
      componentType: StructureComponentType,
      color: string,
   ): void => {
      const entry = globalSettingsRef.current.colors.get(structureType);
      if (entry) {
         if (componentType === 'pocket') entry.pocketColor = color;
         else if (componentType === 'chain') entry.restColor = color;
      }
   };

   /**
    * Subscribes to the representation/color change bus channel.
    *
    * **Global change (`payload.all === true`)**:
    * 1. Updates `globalSettingsRef` so future `onLoaded` callbacks apply the right settings.
    * 2. Mirrors the change into every entry in `controlsMap` via `setControl`.
    * 3. For pocket/ligand changes: calls `updateByComponentType` per structure (lazy
    *    component initialization may be needed). When `resetTransparency` is set, alpha
    *    is restored to 1.0 for every visible structure after those updates resolve.
    * 4. For chain changes: calls the batch `changeRepresentation` / `changeColor` methods.
    *    `resetTransparency` is applied the same way after the chain update.
    *
    * **Per-row change (`payload.rowId` set)**:
    * Calls `updateByComponentType` for the single structure. `postAlpha` is forwarded as
    * the `alpha` parameter so color and alpha are committed atomically.
    */
   const subscribeRepresentationChange = (): (() => void) => {
      return subscribeToRepresentationChange((payload: MolstarRepresentationChangePayload) => {
         const wrapper = wrapperRef.current;
         if (!wrapper || !wrapper.plugin) return;

         if (payload.allStructures || payload.allStructuresOfSingleType) {
            if (payload.representationChanged) {
               if (payload.structureComponentType === 'pocket') {
                  globalSettingsRef.current.pocketRepresentation = payload.type;
               } else if (payload.structureComponentType === 'ligand') {
                  globalSettingsRef.current.ligandRepresentation = payload.type;
               } else {
                  globalSettingsRef.current.restRepresentation = payload.type;
               }
            }
            // Per-group events update the per-type defaults so future loads get the right color.
            // Global (allStructures) events also update all group defaults (structureType=undefined).
            if (payload.colorChanged && payload.allStructuresOfSingleType) {
               assignGlobalColor(payload.structureType, payload.structureComponentType, payload.color);
            } else if (payload.colorChanged && payload.allStructures) {
               assignGlobalColor(undefined, payload.structureComponentType, payload.color);
            }
            // Mirror the change into every matching row's controlsMap entry
            for (const [rowId, state] of controlsMap) {
               // Filter to the target group when this is a per-group event
               if (payload.allStructuresOfSingleType && payload.structureType) {
                  const group = getStructureTypeGroup(state.structureType);
                  if (group !== payload.structureType) continue;
               }
               const updated = { ...state };
               if (payload.representationChanged) {
                  updated.selectedRepresentations = new Map(state.selectedRepresentations);
                  updated.selectedRepresentations.set(payload.structureComponentType, payload.type);
               }
               if (payload.colorChanged) {
                  if (payload.structureComponentType === 'pocket') updated.pocketColor = payload.color;
                  else if (payload.structureComponentType === 'ligand') updated.ligandColor = payload.color;
                  else updated.chainColor = payload.color;
               }
               setControl(rowId, updated);
            }

            if (payload.structureComponentType === 'pocket' || payload.structureComponentType === 'ligand') {
               // Pocket and ligand use per-structure updateByComponentType to bootstrap components.
               // Await those updates before resetTransparency: a color-theme commit does not
               // touch type.params.alpha, so faded quick-color alphas would otherwise stick.
               (async (): Promise<void> => {
                  const updates: Promise<void>[] = [];
                  for (const [label, ref] of wrapper.structureReferences) {
                     // Filter by group for per-group events
                     if (payload.allStructuresOfSingleType && payload.structureType) {
                        const group = getStructureTypeGroup(ref.site.type);
                        if (group !== payload.structureType) continue;
                     }
                     updates.push(
                        wrapper.updateByComponentType(
                           label,
                           payload.type,
                           payload.color,
                           residueMapping.get(ref.site.id) ?? ({} as PocketResiduesResult),
                           payload.structureComponentType,
                           payload.representationChanged,
                           payload.colorChanged,
                        ),
                     );
                  }
                  await Promise.all(updates);
                  if (payload.resetTransparency) {
                     for (const [label, ref] of wrapper.structureReferences) {
                        if (ref.isVisible) {
                           await wrapper.setStructureTransparency(label, 1.0);
                        }
                     }
                  }
               })().catch((err) => {
                  console.error('updateByComponentType (all pocket/ligand) failed:', err);
               });
            } else {
               // Chain changes.
               // For global (allStructures) events, use the batch methods — faster.
               // For per-group (allStructuresOfSingleType) events, iterate per structure so we can filter.
               if (payload.allStructures) {
                  (async (): Promise<void> => {
                     if (payload.representationChanged) {
                        await wrapper.changeRepresentation(payload.type, payload.structureComponentType);
                     }
                     if (payload.colorChanged) {
                        await wrapper.changeColor(payload.color, payload.structureComponentType);
                     }
                     if (payload.resetTransparency) {
                        for (const [label, ref] of wrapper.structureReferences) {
                           if (ref.isVisible) {
                              await wrapper.setStructureTransparency(label, 1.0);
                           }
                        }
                     }
                  })().catch((err) => {
                     console.error('changeRepresentation/changeColor (global) failed:', err);
                  });
               } else {
                  // Per-group chain update: updateByComponentType per structure with group filter.
                  (async (): Promise<void> => {
                     for (const [label, ref] of wrapper.structureReferences) {
                        if (payload.structureType) {
                           const group = getStructureTypeGroup(ref.site.type);
                           if (group !== payload.structureType) continue;
                        }
                        await wrapper.updateByComponentType(
                           label,
                           payload.type,
                           payload.color,
                           residueMapping.get(ref.site.id) ?? ({} as PocketResiduesResult),
                           payload.structureComponentType,
                           payload.representationChanged,
                           payload.colorChanged,
                        );
                     }
                     if (payload.resetTransparency) {
                        for (const [label, ref] of wrapper.structureReferences) {
                           if (ref.isVisible) {
                              await wrapper.setStructureTransparency(label, 1.0);
                           }
                        }
                     }
                  })().catch((err) => {
                     console.error('updateByComponentType (per-group chain) failed:', err);
                  });
               }
            }
         } else if (payload.rowId) {
            const label = payload.url.split('/').pop() || '';
            // postAlpha is passed as the alpha arg so color + alpha are committed
            // atomically inside updateByComponentType, avoiding any race where
            // updateRepresentationsTheme resets alpha after a separate setStructureTransparency.
            wrapper
               .updateByComponentType(
                  label,
                  payload.type,
                  payload.color,
                  residueMapping.get(payload.rowId ?? '') ?? ({} as PocketResiduesResult),
                  payload.structureComponentType,
                  payload.representationChanged,
                  payload.colorChanged,
                  payload.postAlpha,
               )
               .catch((err) => {
                  console.error('updateByComponentType failed:', err);
               });
         }
      });
   };

   useEffect(() => {
      controlsMapRef.current = controlsMap;
   }, [controlsMap]);

   useEffect(() => {
      init().catch((err) => console.error(err));

      const unsubscribe = subscribeVisibilityChange();
      const unsubscribeRepresentationChange = subscribeRepresentationChange();

      return () => {
         unsubscribe();
         unsubscribeRepresentationChange();
      };
   }, []);

   return <div id="molstar-parent" style={{ width: '100%', height: '100%', position: 'relative' }}></div>;
};

export default MolstarComponent;
